import { computed, ref, watch } from "vue";
import debounce from "lodash/debounce";
import { RangeRenderState } from "instantsearch.js/es/connectors/range/connectRange";

export type RangeInputState = Pick<RangeRenderState, 'canRefine' | 'range'> & {
    currentRefinement: RangeRenderState['range'],
    refine: (value: RangeRenderState['range']) => void,
}

export function useFilterPrice(props: { state: RangeInputState })
{
    const range = computed(() => ({
        min: Math.floor((props.state.range.min!) / 100),
        max: Math.ceil((props.state.range.max!) / 100),
    }))

    const sliderValue = ref<[number, number]>([0,0]);
    const inputValue = ref<[number, number]>([0,0]);
    let changeOrigin: 'slider'|'input'|null = null;

    watch(() => JSON.stringify([props.state.currentRefinement, props.state.range]),() => {
        sliderValue.value = [
            typeof props.state.currentRefinement.min === 'number' ? props.state.currentRefinement.min / 100 : range.value.min,
            typeof props.state.currentRefinement.max === 'number' ? props.state.currentRefinement.max / 100 : range.value.max,
        ];
        if(changeOrigin !== 'input') {
            inputValue.value = [...sliderValue.value];
        }
        changeOrigin = null;
    }, { immediate: true });

    const refineSlider = debounce(value => refine(value, 'slider'), 200);
    function onSliderChange() {
        inputValue.value = [...sliderValue.value];
        refineSlider([...sliderValue.value]);
    }

    const refineInput = debounce(value => refine(value, 'input'), 500);
    function onInputChange() {
        sliderValue.value[0] = Math.max(range.value.min, Math.min(inputValue.value[0], range.value.max));
        sliderValue.value[1] = Math.max(range.value.min, Math.min(inputValue.value[1], range.value.max));
        refineInput([...sliderValue.value]);
    }

    function refine (value: [number, number], origin: typeof changeOrigin) {
        changeOrigin = origin;
        props.state.refine({
            min: value[0] === range.value.min ? undefined : value[0] * 100,
            max: value[1] === range.value.max ? undefined : value[1] * 100,
        });
    }

    return {
        range,
        sliderValue,
        inputValue,
        onSliderChange,
        onInputChange,
    }
}
